import * as React from "react";
import { SectionShell } from "@/components/design-system/SectionShell";
import { QuantityStepper } from "@/components/ui/quantity-stepper";

const code = `import { QuantityStepper } from "@/components/ui/quantity-stepper";
import { router } from "@inertiajs/react";

// Controlled: the caller owns the value and posts the change.
<QuantityStepper
  label={\`Bottles of \${wine.name}\`}
  value={bottleCount}
  onChange={(next) =>
    router.patch(\`/selection/lines/\${wine.id}\`, { bottle_count: next }, {
      preserveState: true,
      preserveScroll: true,
    })
  }
  size="sm"
/>`;

export function QuantityStepperSection() {
  const [small, setSmall] = React.useState(0);
  const [medium, setMedium] = React.useState(12);

  return (
    <SectionShell
      id="quantity-stepper"
      title="Quantity stepper"
      description={
        <>
          A whole-number −/value/+ control for single-bottle quantities. The
          buttons move one at a time; the number can also be typed and commits
          on blur or Enter. Square, hairline and charcoal like{" "}
          <code>.form-control-cellar</code>, with a gold focus ring. A polite{" "}
          <code>aria-live</code> region reads the new value out.
        </>
      }
      whenToUse={
        <ul>
          <li>Adding bottles of a wine to the visitor&rsquo;s selection (cards, detail page, order review, tray).</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Free numeric answers with no natural step (a budget, a bottle estimate on a form) — use <code>Input</code>.</li>
          <li>Case multiples — quantities are always per single bottle.</li>
        </ul>
      }
      preview={
        <div className="grid gap-8 md:grid-cols-2">
          <div className="bg-charcoal p-6">
            <label htmlFor="ds-stepper-sm" className="field-label">Size sm</label>
            <QuantityStepper
              id="ds-stepper-sm"
              label="Bottles (small example)"
              size="sm"
              value={small}
              onChange={setSmall}
            />
            <div className="field-hint">{small} bottles</div>
          </div>
          <div className="bg-page p-6">
            <label htmlFor="ds-stepper-md" className="field-label">Size md (default)</label>
            <QuantityStepper
              id="ds-stepper-md"
              label="Bottles (medium example)"
              value={medium}
              onChange={setMedium}
            />
            <div className="field-hint">{medium} bottles</div>
          </div>
        </div>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li><code>value</code> + <code>onChange(next)</code> — required, controlled. <code>onChange</code> only fires when the clamped value actually changes.</li>
          <li><code>label</code> — required accessible name for the group and the input.</li>
          <li><code>size</code>: <code>sm</code> (40px) | <code>md</code> (48px, default).</li>
          <li><code>min</code> (default 0), <code>max</code> (optional), <code>unit</code> (default &ldquo;bottles&rdquo;), <code>id</code>, <code>name</code>, <code>disabled</code>, <code>className</code>.</li>
          <li>No dependency on selection state — the caller wires the posting.</li>
        </ul>
      }
    />
  );
}
